import { apiClient, handleApiRequest } from './client'
import { teacherChatApi as teacherChatApiDelegated } from './teacherChat'
import type {
  TeacherAvailability,
  TimeSlotInfo,
  PrepLinkResponse,
  PrepAttachmentResponse,
  LessonPrepStatusResponse,
  ChatParticipantResponse,
  PrepRequestResponse,
  BasicCascadePreviewResponse,
} from '@/types/teacher-request'

export interface PriorityEntryCreate {
  teacher_id: string
  priority_rank: number
}

export interface CoverageBlockCreate {
  time_slot_ids: string[]
  block_name?: string
  block_type: 'full_day' | 'partial'
  block_rank: number
  date?: string
  package_id?: string
  request_mode?: 'sequential' | 'parallel'
  custom_threshold_minutes?: number
  entries?: PriorityEntryCreate[]
}

export interface PriorityRequestCreate {
  entry_ids: string[]
  date: string
  date_to?: string | null
  lessons?: { entry_id: string; date: string }[]
  coverage_blocks?: CoverageBlockCreate[]
  request_mode?: 'sequential' | 'parallel'
  custom_threshold_minutes?: number | null
  priority_fallback_mode?: 'auto' | 'manual' | null
  priority_cascade_strategy?: 'full_day_first' | 'unfilled_only' | null
  parallel_deadline_minutes?: number | null
  message_to_substitutes?: string
  is_urgent?: boolean
  // R3: Grund/Bemerkung — nur für den Direktor sichtbar (E12)
  reason?: string
  // Reparatur-Fix C10: string statt 6-Literal-Union — Schulen definieren
  // eigene Abwesenheitsarten (absence_types-Tabelle); Backend validiert.
  absence_type?: string
  request_flow?: 'prioritized' | 'automatic'
  week_rank_mode?: 'repeat' | 'each'
}

export interface PriorityRequestResponse {
  message: string
  request_id: string
  status: string
  lesson_count: number
  request_flow?: 'prioritized' | 'automatic'
}

export interface PriorityEntryResponse {
  id: string
  coverage_block_id: string
  teacher_id: string
  priority_rank: number
  status: string
  contacted_at: string | null
  responded_at: string | null
  timeout_at: string | null
  teacher_short_code?: string | null
  teacher_name?: string | null
  rejection_comment?: string | null
  provisional_lesson_ids?: string[]
  commitment_level?: 'provisional' | 'definitive' | null
  interest_status?: string | null
}

export interface LessonPackageResponse {
  id: string
  date: string | null
  time_slot_ids: string[]
  time_slot_count: number
  lesson_count?: number
  total_lessons?: number
  coverage_percentage?: number
  status: string
  package_type: string
  package_rank?: number
  background_id?: string | null
  assigned_teacher_id?: string | null
  assigned_teacher_name?: string | null
  assigned_teacher_short_code?: string | null
  teacher_name?: string | null
  teacher_id?: string | null
  is_full_day?: boolean
  created_at?: string
  interested_count?: number
  not_interested_count?: number
  // NEU: Neue Felder für Didaktik V3
  lessons?: Array<{
    id: string
    subject: string
    klass: string
    room: string
  }>
  original_lesson_count?: number
  original_lesson_ids?: string[]
  has_history?: boolean
  cascade?: {
    entries: Array<{
      teacher_code: string
      status: string
      priority_rank: number
    }>
  }
}

export interface CoverageBlockResponse {
  id: string
  request_id: string
  date: string | null
  block_name: string | null
  block_type: string
  block_rank: number
  time_slot_ids: string[]
  package_id: string | null
  request_mode: string
  custom_threshold_minutes: number | null
  status: string
  cascade_started_at: string | null
  entries: PriorityEntryResponse[]
  created_at: string
  updated_at: string
}

export interface RequestStatusResponse {
  request_id: string
  status: string
  composite_status: string
  request_mode: string
  request_flow?: 'prioritized' | 'automatic'
  requesting_teacher_id: string
  substitute_teacher_id?: string
  coverage_blocks: CoverageBlockResponse[]
  packages: LessonPackageResponse[]
  live_updates: Record<string, unknown>[]
  subject_name?: string | null
  klass_name?: string | null
  room_name?: string | null
  is_urgent?: boolean
  date?: string | null
  date_to?: string | null
  lesson_count?: number
  total_lessons?: number
}

export interface PackageCreate {
  date: string
  time_slot_ids: string[]
  package_rank?: number | null
}

export interface PackageResponse {
  message: string
  package_id: string
  time_slot_count: number
}

export interface AutoPackagesResponse {
  message: string
  package_count: number
}

export interface PrepAcceptanceInfo {
  teacher_id: string
  teacher_name: string
  accepted_at: string
}

export interface PrepResponse {
  id: string
  request_id: string
  title: string
  description: string | null
  duration_minutes: number | null
  notes: string | null
  created_at: string
  updated_at: string
  linked_lessons?: Array<{ id: string; lesson_request_id: string; date: string; time_slot_name: string; klass_name?: string }>
  linked_packages?: Array<{ id: string; package_id: string; date: string; time_slot_names: string[] }>
  attachment_count?: number
  total_attachment_size?: number
  /** prepgruen: Akzeptanz-Anreicherung (live berechnet) */
  acceptance_count?: number
  total_affected_teachers?: number
  fully_accepted?: boolean
  accepted_by_current_teacher?: boolean
  current_teacher_pending_acceptance?: boolean
  acceptances?: PrepAcceptanceInfo[]
}

export interface PrepAcceptResponse {
  accepted: boolean
  already_accepted?: boolean
  acceptance_count: number
  total_affected: number
  fully_accepted: boolean
}

export interface MessageResponse {
  message: string
  success: boolean
  /** noscore G2: present when an assignment was performed as a conscious
   *  override for unqualified lessons (binary qualification warning). */
  warning?: string | null
}

export interface SubmitPriorityResponse extends MessageResponse {
  uncovered_lesson_count?: number
  needs_forward_decision?: boolean
  behavior?: string | null
  /** R4/B1: effektiver Status nach dem Submit (z.B. 'director_pending') */
  request_status?: string
}

export interface CancellationItemResponse {
  id: string
  coverage_block_id: string | null
  lesson_request_id: string | null
  responding_teacher_id: string
  status: string
  responded_at: string | null
  is_mine?: boolean | null
}

export interface CancellationRequestResponse {
  id: string
  substitution_request_id: string
  requesting_teacher_id: string
  responding_teacher_id: string | null
  initiating_role?: string | null
  re_dispatch?: boolean | null
  reason: string | null
  status: string
  created_at: string
  responded_at: string | null
  expires_at?: string | null
  items?: CancellationItemResponse[]
}

export interface ReRequestResponse {
  message: string
  new_request_id: string
  original_request_id: string
}

export interface CoverageSummaryItem {
  teacher_id: string
  teacher_short_code?: string | null
  status: string
  priority_rank: number
}

export interface BlockSummaryItem {
  block_id: string
  id?: string
  date: string
  subject_name?: string | null
  klass_name?: string | null
  room_name?: string | null
  time_slot_names?: string[]
  time_slot_ids?: string[]
  start_time?: string | null
  end_time?: string | null
  status: string
  accepted_teacher_code?: string | null
  contacted_count: number
  total_entries: number
  progress_percentage: number
  entries: CoverageSummaryItem[]
  // Neue Felder für didaktische Karte
  lesson_count?: number
  lessons?: Array<{
    id: string
    subject: string
    klass: string
    room: string
    time_slot_name?: string | null
    assigned_teacher_id?: string | null
    assigned_teacher_short_code?: string | null
    assignment_state?: string | null
  }>
  original_lesson_count?: number
  original_lesson_ids?: string[]
  has_history?: boolean
  cascade?: {
    entries: Array<{
      teacher_code: string
      status: string
      priority_rank: number
    }>
  }
  // prio3-v2: Multi-Week + Homogeneity
  is_multi_week?: boolean
  occurrence_dates?: string[] | null
  multi_week_group_id?: string | null
  homogeneity_score?: number | null
}

export interface CoverageSummary {
  accepted: number
  declined: number
  waiting: number
  teachers: CoverageSummaryItem[]
  cascade_status?: string | null
  block_summaries?: BlockSummaryItem[]
  teachers_pending?: number
}

export interface PriorityRequestListItem {
  id: string
  date: string
  date_to?: string | null
  status: string
  lesson_count: number
  request_mode: string
  request_flow?: 'prioritized' | 'automatic'
  request_flow_strategy?: string | null
  phase?: string | null
  created_at: string
  expires_at: string | null
  current_answer_deadline?: string | null
  is_urgent: boolean
  subject_name?: string | null
  klass_name?: string | null
  room_name?: string | null
  time_slot_name?: string | null
  composite_status?: string
  has_open_prep_request?: boolean
  coverage_summary?: CoverageSummary | null
  cancelled_at?: string | null
  cancellation_pending?: boolean
  cascade_preview_enabled?: boolean
  max_visible_cascade_badges?: number
  /** prio-v2 (P1/E1): Fallback-Bestätigung wartet auf den anfragenden Lehrer */
  fallback_confirmation_pending?: boolean
  combo_search_pending?: boolean
  uncovered_dates?: string[] | null
  /** modal-v2 (E8/EC3): Dokumente-Status (Badge «Dokumente ausstehend» + Nachreich) */
  documents_requested_at?: string | null
  documents_deferred?: boolean
  documents_received?: boolean
  document_due?: boolean
  has_document?: boolean
  director_decision_type?: 'approved' | 'rejected' | 'returned' | 'info_requested' | null
  director_payment_type?: 'private' | 'school' | null
  director_enabled?: boolean
  requester_deduction?: 'ipb' | 'salary' | null
  can_change_requester_finance?: boolean
  hr_module_enabled?: boolean
  requester_decides?: boolean
  requester_school_default?: 'ipb' | 'salary' | null
  finance_binding_at?: string | null
  finance_lock_reason?:
    | 'decider_hr'
    | 'not_in_play'
    | 'settled'
    | 'hr_override'
    | 'binding'
    | 'payer_not_private'
    | null
  show_requester_finance_hint?: boolean
  hr_enabled?: boolean
  created_on_behalf?: boolean
  created_by_name?: string | null
  request_source?: string | null
}

export interface PriorityRequestListResponse {
  requests: PriorityRequestListItem[]
  total: number
}

export interface TeacherAvailabilityResponse {
  date: string
  teachers: TeacherAvailability[]
  time_slots: TimeSlotInfo[]
  /** noscore: fail-closed NO_DATA diagnosis hints (empty teacher list) */
  no_data_hints?: string[]
  groups?: AvailabilityGroupInfo[]
  filters_applied?: {
    subject_id?: string
    background_id?: string
    subject_name?: string
    background_name?: string
    entry_ids?: string[]
    entry_groups?: number
  }
  error?: string
}

export interface AvailabilityGroupInfo {
  subject_id?: string | null
  background_id?: string | null
  subject_name?: string | null
  background_name?: string | null
  time_slot_ids: string[]
  teacher_ids: string[]
}

export interface MultiDateAvailabilityResponse {
  date_range: { from: string; to: string }
  days: Record<string, {
    teachers: TeacherAvailability[]
    time_slots: TimeSlotInfo[]
    groups?: AvailabilityGroupInfo[]
    no_data_hints?: string[]
  }>
  filters_applied?: {
    subject_id?: string
    background_id?: string
    subject_name?: string
    background_name?: string
    entry_ids?: string[]
    entry_groups?: number
  }
  all_time_slots: TimeSlotInfo[]
  all_teachers: TeacherAvailability[]
}

export const teacherPriorityApi = {
  listRequests: (
    statusFilter?: string,
    skip?: number,
    limit?: number,
    dateFrom?: string,
    dateTo?: string,
    requestId?: string
  ) => {
    const qs = new URLSearchParams()
    if (statusFilter) qs.set('status_filter', statusFilter)
    if (skip !== undefined) qs.set('skip', String(skip))
    if (limit !== undefined) qs.set('limit', String(limit))
    if (dateFrom) qs.set('date_from', dateFrom)
    if (dateTo) qs.set('date_to', dateTo)
    if (requestId) qs.set('request_id', requestId)
    const q = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<PriorityRequestListResponse>(
      apiClient.get(`/api/v1/teacher-requests${q}`)
    )
  },

  getAvailability: (date: string, entryIds?: string[], timeSlotId?: string) => {
    const qs = new URLSearchParams({ date })
    if (entryIds?.length) qs.set('entry_ids', entryIds.join(','))
    if (timeSlotId) qs.set('time_slot_id', timeSlotId)
    return handleApiRequest<TeacherAvailabilityResponse>(
      apiClient.get(`/api/v1/teacher-requests/availability?${qs.toString()}`)
    )
  },

  getAvailabilityRange: (from: string, to: string, entryIds?: string[]) => {
    const qs = new URLSearchParams({ date_from: from, date_to: to })
    if (entryIds?.length) qs.set('entry_ids', entryIds.join(','))
    return handleApiRequest<MultiDateAvailabilityResponse>(
      apiClient.get(`/api/v1/teacher-requests/availability?${qs.toString()}`)
    )
  },

  /**
   * Load availability for selected lesson dates.
   * Span limit is Superadmin `max_days_per_request` (enforced by the API).
   */
  getAvailabilityForDates: (
    dates: string[],
    entryIdsByDate: Record<string, string[]>,
  ): Promise<MultiDateAvailabilityResponse> => {
    const unique = [...new Set(dates.filter(Boolean))].sort()
    if (unique.length === 0) {
      return Promise.resolve({
        date_range: { from: '', to: '' },
        days: {},
        all_time_slots: [],
        all_teachers: [],
      })
    }
    const ids = unique.flatMap((d) => entryIdsByDate[d] || [])
    return teacherPriorityApi.getAvailabilityRange(
      unique[0]!,
      unique[unique.length - 1]!,
      ids.length > 0 ? ids : undefined,
    )
  },

  // preview-v5: optionale Absenzdaten — Singleton (date) oder Range
  // (dateFrom/dateTo). Ohne Angaben nutzt das Backend den Entry-Fallback.
  getQualifiedTeachersCount: (
    entryIds: string[],
    opts?: { date?: string; dateFrom?: string; dateTo?: string }
  ) => {
    const qs = new URLSearchParams()
    if (opts?.date) qs.set('date', opts.date)
    if (opts?.dateFrom) qs.set('date_from', opts.dateFrom)
    if (opts?.dateTo) qs.set('date_to', opts.dateTo)
    const extra = qs.toString()
    return handleApiRequest<{ count: number }>(
      apiClient.get(`/api/v1/teacher-requests/qualified-teachers-count?entry_ids=${entryIds.join(',')}${extra ? `&${extra}` : ''}`)
    )
  },

  createRequest: (data: PriorityRequestCreate) =>
    handleApiRequest<PriorityRequestResponse>(
      apiClient.post('/api/v1/teacher-requests', data)
    ),

  submitRequest: (requestId: string, data?: { documents_deferred?: boolean }) =>
    handleApiRequest<SubmitPriorityResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/submit`, data ?? {})
    ),

  // E6-E11: Dokumente/Atteste einer Anfrage (Eigentümer + geroutete Direktoren)
  getRequestDocuments: (requestId: string) =>
    handleApiRequest<import('@/types/teacher-request').SubstitutionRequestDocument[]>(
      apiClient.get(`/api/v1/teacher-requests/${requestId}/documents`)
    ),

  uploadRequestDocument: (requestId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return handleApiRequest<import('@/types/teacher-request').SubstitutionRequestDocument>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    )
  },

  deleteRequestDocument: (requestId: string, documentId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.delete(`/api/v1/teacher-requests/${requestId}/documents/${documentId}`)
    ),

  downloadRequestDocument: (requestId: string, documentId: string) =>
    handleApiRequest<Blob>(
      apiClient.get(`/api/v1/teacher-requests/${requestId}/documents/${documentId}/download`, {
        responseType: 'blob',
      })
    ),

  // D1: Direktor-Gate-Settings (Modal-Öffnung E1, Doc-Regeln)
  getDirectorGateSettings: () =>
    handleApiRequest<import('@/types/teacher-request').DirectorGateSettings>(
      apiClient.get('/api/v1/teacher-requests/director-gate-settings')
    ),

  // E5: Gate-Preview (Modal-Infoline, 300ms-Debounce im Aufrufer)
  getDirectorGatePreview: (payload: {
    entry_ids: string[]
    date?: string | null
    date_to?: string | null
    lessons?: { entry_id: string; date: string }[]
    absence_type?: string | null
  }, signal?: AbortSignal) =>
    handleApiRequest<import('@/types/teacher-request').GatePreviewResponse>(
      apiClient.post('/api/v1/teacher-requests/director-gate-preview', payload, { signal })
    ),

  // prio-v2 (P1/E1): Matcher-Fallback nach erschöpfter Prioritätsliste bestätigen
  confirmFallback: (requestId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/confirm-fallback`)
    ),

  // Konzept A — "kein Vertreter verfügbar": an Admin weiterleiten + Empfänger-Pool
  forwardToAdmin: (requestId: string, payload: { recipient_user_ids?: string[]; message?: string }) =>
    handleApiRequest<MessageResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/forward-to-admin`, payload)
    ),

  getNoSubstituteRecipients: () =>
    handleApiRequest<{ recipients: { user_id: string; name: string }[] }>(
      apiClient.get('/api/v1/teacher/no-substitute-recipients')
    ),

  getRequestStatus: (requestId: string) =>
    handleApiRequest<RequestStatusResponse>(
      apiClient.get(`/api/v1/teacher-requests/${requestId}/status`)
    ),

  cancelRequest: (requestId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/cancel`)
    ),

  cancelBlock: (requestId: string, blockId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/blocks/${blockId}/cancel`)
    ),

  undoLastAction: (requestId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/undo`)
    ),

  createPackage: (requestId: string, data: PackageCreate) =>
    handleApiRequest<PackageResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/packages`, data)
    ),

  deletePackage: (requestId: string, packageId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.delete(`/api/v1/teacher-requests/${requestId}/packages/${packageId}`)
    ),

  assignPackage: (requestId: string, packageId: string, teacherId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/packages/${packageId}/assign`, { teacher_id: teacherId })
    ),

  generateAutoPackages: (requestId: string) =>
    handleApiRequest<AutoPackagesResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/packages/generate-auto`)
    ),

  getPackages: (requestId: string) =>
    handleApiRequest<{ packages: LessonPackageResponse[]; teacher_matching_enabled: boolean }>(
      apiClient.get(`/api/v1/teacher-requests/${requestId}/packages`)
    ),

  getPackageHistory: (packageId: string, limit: number = 20, offset: number = 0) =>
    handleApiRequest<{
      package_id: string
      total: number
      limit: number
      offset: number
      history: Array<{
        event_type: string
        event_data?: any
        before_lesson_count?: number
        after_lesson_count?: number
        reason?: string
        occurred_at: string
        created_by: string
      }>
    }>(
      apiClient.get(`/api/v1/teacher-requests/packages/${packageId}/history?limit=${limit}&offset=${offset}`)
    ),

  createPrep: (requestId: string, data: { title: string; description?: string; duration_minutes?: number; notes?: string; lesson_request_ids?: string[] }) =>
    handleApiRequest<PrepResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/prep`, data)
    ),

  getPreps: (requestId: string) =>
    handleApiRequest<PrepResponse[]>(
      apiClient.get(`/api/v1/teacher-requests/${requestId}/prep`)
    ),

  // prepgruen: Prep-Akzeptanz (Einzel + Bulk)
  acceptPrep: (requestId: string, prepId: string) =>
    handleApiRequest<PrepAcceptResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/prep/${prepId}/accept`)
    ),

  deletePrep: (requestId: string, prepId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.delete(`/api/v1/teacher-requests/${requestId}/prep/${prepId}`)
    ),

  updatePrep: (requestId: string, prepId: string, data: { title?: string; description?: string; duration_minutes?: number; notes?: string }) =>
    handleApiRequest<PrepResponse>(
      apiClient.patch(`/api/v1/teacher-requests/${requestId}/prep/${prepId}`, data)
    ),

  // Prep Links
  createPrepLink: (requestId: string, prepId: string, data: { lesson_request_id?: string; package_id?: string }) =>
    handleApiRequest<PrepLinkResponse>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/prep/${prepId}/links`, data)
    ),

  deletePrepLink: (requestId: string, prepId: string, linkId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.delete(`/api/v1/teacher-requests/${requestId}/prep/${prepId}/links/${linkId}`)
    ),

  // Prep Attachments
  getPrepAttachments: (requestId: string, prepId: string) =>
    handleApiRequest<PrepAttachmentResponse[]>(
      apiClient.get(`/api/v1/teacher-requests/${requestId}/prep/${prepId}/attachments`)
    ),

  uploadPrepAttachment: (requestId: string, prepId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return handleApiRequest<PrepAttachmentResponse>(
      apiClient.post(
        `/api/v1/teacher-requests/${requestId}/prep/${prepId}/attachments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
    )
  },

  deletePrepAttachment: (requestId: string, prepId: string, attachmentId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.delete(`/api/v1/teacher-requests/${requestId}/prep/${prepId}/attachments/${attachmentId}`)
    ),

  downloadPrepAttachment: (requestId: string, prepId: string, attachmentId: string) =>
    handleApiRequest<Blob>(
      apiClient.get(
        `/api/v1/teacher-requests/${requestId}/prep/${prepId}/attachments/${attachmentId}/download`,
        { responseType: 'blob' }
      )
    ),

  // Prep Lesson Status
  getLessonPrepStatus: (requestId: string) =>
    handleApiRequest<LessonPrepStatusResponse[]>(
      apiClient.get(`/api/v1/teacher-requests/${requestId}/prep/lesson-status`)
    ),

  // Chat (delegates to canonical /teacher-chats endpoints)
  getChatParticipants: (requestId: string) =>
    handleApiRequest<ChatParticipantResponse[]>(
      apiClient.get(`/api/v1/teacher-chats/${requestId}/participants`)
    ),

  addChatParticipant: (requestId: string, teacherId: string) =>
    handleApiRequest<ChatParticipantResponse>(
      apiClient.post(`/api/v1/teacher-chats/${requestId}/participants`, { teacher_id: teacherId })
    ),

  removeChatParticipant: (requestId: string, teacherId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.delete(`/api/v1/teacher-chats/${requestId}/participants/${teacherId}`)
    ),

  // Dup#3: Chat-Methoden delegieren an den konsolidierten teacherChatApi
  // (Single Source fuer Scope-Param-Serialisierung + Pagination-Contract).
  getChatMessages: (
    requestId: string,
    limit = 50,
    cursor?: import('@/types/teacher-chat').ScopeCursor,
    scopeFilter?: import('@/types/teacher-chat').ScopeFilter
  ) => teacherChatApiDelegated.getMessages(requestId, limit, cursor, scopeFilter),

  sendChatMessage: (requestId: string, body: string, scopeLessonIds?: string[]) =>
    teacherChatApiDelegated.sendMessage(requestId, body, scopeLessonIds),

  // Cancellation request endpoints
  createCancellationRequest: (data: {
    substitution_request_id: string
    reason?: string
    block_ids?: string[]
    lesson_ids?: string[]
  }) =>
    handleApiRequest<CancellationRequestResponse>(
      apiClient.post('/api/v1/cancellation-requests', data)
    ),

  getCancellationRequests: () =>
    handleApiRequest<CancellationRequestResponse[]>(
      apiClient.get('/api/v1/cancellation-requests')
    ),

  acceptCancellationRequest: (cancellationId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.post(`/api/v1/cancellation-requests/${cancellationId}/accept`)
    ),

  declineCancellationRequest: (cancellationId: string) =>
    handleApiRequest<MessageResponse>(
      apiClient.post(`/api/v1/cancellation-requests/${cancellationId}/decline`)
    ),

  // Re-request endpoint
  reRequest: (
    requestId: string,
    excludeTeacherIds?: string[],
    forceAll?: boolean
  ) => {
    const params = new URLSearchParams()
    if (excludeTeacherIds?.length) {
      excludeTeacherIds.forEach(id => params.append('exclude_teacher_ids', id))
    }
    if (forceAll) {
      params.set('force_all', 'true')
    }
    
    return handleApiRequest<ReRequestResponse>(
      apiClient.post(
        `/api/v1/teacher-requests/${requestId}/re-request?${params.toString()}`
      )
    )
  },

  // Prep Request (Phase 5)
  requestPrep: (requestId: string, data: { lesson_request_ids?: string[]; reason?: string }) =>
    handleApiRequest<PrepRequestResponse[]>(
      apiClient.post(`/api/v1/teacher-requests/${requestId}/prep/request`, data)
    ),

  getPrepRequests: (requestId: string) =>
    handleApiRequest<PrepRequestResponse[]>(
      apiClient.get(`/api/v1/teacher-requests/${requestId}/prep/requests`)
    ),

  resolvePrepRequest: (requestId: string, prepRequestId: string, data: { status: string }) =>
    handleApiRequest<PrepRequestResponse>(
      apiClient.patch(`/api/v1/teacher-requests/${requestId}/prep/request/${prepRequestId}`, data)
    ),

  getCascadePreview: (requestId: string) =>
    handleApiRequest<BasicCascadePreviewResponse>(
      apiClient.get(`/api/v1/teacher-requests/${requestId}/cascade-preview`)
    ),

  getRequestLessons: (requestId: string) =>
    handleApiRequest<{ request_id: string; lesson_request_ids: string[] }>(
      apiClient.get(`/api/v1/teacher-requests/${requestId}/lessons`)
    ),

    removeLesson: (requestId: string, lessonId: string) =>
    handleApiRequest<{ message: string; success: boolean }>(
      apiClient.delete(`/api/v1/teacher-requests/${requestId}/lessons/${lessonId}`)
    ),
}
